/**
 * TwilioTestSmsCard — CEO/Admin one-tap "Send test SMS to my phone".
 *
 * Uses the shared `notification_service.send_sms` pipeline (Messaging Service
 * SID + E.164 + A2P STOP footer), so it's a true production-parity check.
 * Perfect for verifying carrier deliverability the moment Twilio approves
 * your A2P campaign.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { MessageCircle, Send, CheckCircle2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TwilioTestSmsCard({ user }) {
  const [phone, setPhone] = useState(user?.phone || '');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const send = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const res = await fetch(`${API}/api/broadcasts/test-sms`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Test SMS failed');
        setLastResult({ ok: false, ...data });
        return;
      }
      setLastResult({ ok: true, ...data });
      if (data.sent) {
        toast.success(`Test SMS sent — Twilio SID ${(data.sid || '').slice(0, 8)}…`);
      } else if (data.mode === 'test') {
        toast.info('TEST-MODE — Twilio keys not configured yet. Message was logged only.');
      } else {
        toast.error(data.reason || data.error || 'Twilio rejected the message');
      }
    } catch (err) {
      toast.error('Network error — check backend logs.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border border-[#C9A962]/40" data-testid="twilio-test-sms-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-[#C9A962]" />
          Twilio Deliverability Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-zinc-400">
          Sends a real SMS through the production pipeline (Messaging Service SID +
          E.164 formatting + A2P STOP footer) to verify carrier delivery. Perfect
          right after your A2P campaign gets approved.
        </p>

        <div className="flex gap-2">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={user?.phone || '+18882413077'}
            className="bg-zinc-800 border-zinc-700 text-white flex-1"
            data-testid="twilio-test-sms-phone"
          />
          <Button
            onClick={send}
            disabled={loading}
            className="bg-[#C9A962] hover:bg-[#b89651] text-black font-bold"
            data-testid="twilio-test-sms-send-btn"
          >
            <Send className="h-4 w-4 mr-1" />
            {loading ? 'Sending…' : 'Send test'}
          </Button>
        </div>

        {lastResult && (
          <div
            className={`rounded-md p-2 text-xs border ${
              lastResult.sent
                ? 'bg-emerald-900/30 border-emerald-700 text-emerald-200'
                : lastResult.mode === 'test'
                ? 'bg-amber-900/30 border-amber-700 text-amber-200'
                : 'bg-red-900/30 border-red-700 text-red-200'
            }`}
            data-testid="twilio-test-sms-result"
          >
            {lastResult.sent && (
              <>
                <div className="flex items-center gap-1 font-semibold">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Sent to {lastResult.to}
                </div>
                <div className="mt-1 font-mono">Twilio SID: {lastResult.sid}</div>
              </>
            )}
            {!lastResult.sent && lastResult.mode === 'test' && (
              <>
                <div className="flex items-center gap-1 font-semibold">
                  <AlertCircle className="h-3.5 w-3.5" /> TEST-MODE
                </div>
                <div className="mt-1">
                  Twilio credentials are not configured on this environment. The message
                  was logged only — check backend logs for the payload.
                </div>
              </>
            )}
            {!lastResult.sent && lastResult.mode !== 'test' && (
              <>
                <div className="flex items-center gap-1 font-semibold">
                  <AlertCircle className="h-3.5 w-3.5" /> Not delivered
                </div>
                <div className="mt-1">
                  {lastResult.reason || lastResult.error || lastResult.detail || 'Unknown error'}
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
          <Badge className="bg-zinc-800 text-zinc-300 border border-zinc-700 text-[10px]">A2P 10DLC</Badge>
          <span>Uses the exact production SMS code path — STOP footer auto-applied.</span>
        </div>
      </CardContent>
    </Card>
  );
}
